import { useState } from "react";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { getUserPreferences } from "src/common/util/getUserPreferences";
import { enqueuePreferencesPatch } from "src/features/profile/state/preferencesWriter";
import { getCurrentUser, getPreferencesResyncing } from "src/features/profile/state/profileSlice";
import type { IUser } from "src/model/model";
import type { SidebarItemDef, SidebarItemId } from "./sidebarConfig";

const readFavoriteIds = (user?: IUser): string[] => {
  const favorites = getUserPreferences({ currentUser: user })?.sidebarFavorites;
  return Array.isArray(favorites) ? favorites : [];
};

const sameOrder = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

export const useSidebarFavorites = (visibleItems: SidebarItemDef[]) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(getCurrentUser);
  const resyncing = useAppSelector(getPreferencesResyncing);
  const [frozen, setFrozen] = useState<string[] | null>(null);

  const storedIds = readFavoriteIds(currentUser);
  const favoriteIds = frozen ?? storedIds;
  const favoritableItems = visibleItems.filter((item) => item.favoritable);
  const favoriteItems = favoriteIds.flatMap((id) => {
    const item = favoritableItems.find((candidate) => candidate.id === id);
    return item ? [item] : [];
  });

  const currentIds = () => frozen ?? storedIds;

  const commit = (next: string[]) => {
    void enqueuePreferencesPatch(dispatch, { sidebarFavorites: next });
  };

  const withHiddenIds = (visibleOrder: string[], base: string[]) => [
    ...visibleOrder,
    ...base.filter((id) => !favoritableItems.some((item) => item.id === id)),
  ];

  const isFavorite = (id: SidebarItemId) => favoriteIds.includes(id);

  const toggleFavorite = (id: SidebarItemId) => {
    if (!favoritableItems.some((item) => item.id === id)) return;
    const base = currentIds();
    commit(base.includes(id) ? base.filter((favId) => favId !== id) : [...base, id]);
  };

  const setFavoritesOrder = (ids: SidebarItemId[]) => {
    if (
      sameOrder(
        ids,
        favoriteItems.map((item) => item.id),
      )
    )
      return;
    commit(withHiddenIds(ids, currentIds()));
  };

  const addFavoriteAt = (id: SidebarItemId, index: number) => {
    if (!favoritableItems.some((item) => item.id === id)) return;
    const visibleOrder = favoriteItems.map((item) => item.id).filter((favId) => favId !== id);
    const position = Math.max(0, Math.min(index, visibleOrder.length));
    visibleOrder.splice(position, 0, id);
    commit(withHiddenIds(visibleOrder, currentIds()));
  };

  const beginDrag = () => setFrozen(favoriteIds);

  const endDrag = () => setFrozen(null);

  return {
    favoriteItems,
    isFavorite,
    toggleFavorite,
    setFavoritesOrder,
    addFavoriteAt,
    beginDrag,
    endDrag,
    resyncing,
  };
};
